import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  HostListener,
  Inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  QueryList,
  SimpleChanges,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import moment from 'moment';

import { MatTooltip } from '@angular/material/tooltip';
import { getEnumValues } from '@common';
import { InstallationDetailsStore, UnitsStore } from '@store';
import Highcharts from 'highcharts';
import * as _ from 'lodash-es';
import { Subject } from 'rxjs';
import { take } from 'rxjs/operators';
import { matTooltipSetter } from 'src/app/shared/functions/mat-tooltip-setter/mat-tooltip-setter';
import { FeaturePropertyKey } from '../../../../projects/client';
import {
  Chart,
  ChartBarPartModel,
  ChartPeriod,
} from '../../../features/chart-bar/models/chart.model';
import { convertToCubicFeet } from '../../../features/temperature/converter/converter';
import { CONSTUMPTION_CHARTS_UNITS_ADAPTER_IT } from '../../../operational-data-common/services/units-adapter/units-adapter.module';
import { UnitsAdapter } from '../../../operational-data-common/services/units-adapter/units.adapter';
import { DAYS_SHORT, MONTHS_SHORT, WEEKDAYS } from '../../../shared/common/enums';
import { getFormatterForLocalizedDate } from '../energy-historical-data-modal/energy-history-chart/chart-options';
import { FeaturesDetailsOverlayTypeEnum } from '../installation-overlay.model';

export const viChartColors = {
  'vi-pending-00': '#a11919',
  'vi-pending-01': '#ff9079',
  'vi-pending-02': '#ff7f64',
  'vi-pending-03': '#ff6545',
  'vi-pending-04': '#d74227',
  'vi-pending-05': '#af1809',
  'vi-pending-06': '#880000',
  'vi-pending-07': '#650000',
  'vi-pending-08': '#5a0000',
  'vi-pending-09': '#4c0000',
};

@Component({
  selector: 'app-generic-consumption-chart-panel',
  templateUrl: './generic-consumption-chart-panel.component.html',
  styleUrls: ['./generic-consumption-chart-panel.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GenericConsumptionChartPanelComponent implements OnInit, OnChanges, OnDestroy {
  readonly featurePropertyKey = FeaturePropertyKey;

  @ViewChild('container') container: ElementRef;

  availableOverlays = FeaturesDetailsOverlayTypeEnum;

  @Input() title: string;
  @Input() chartData: Chart;
  @Input() overlayType: FeaturesDetailsOverlayTypeEnum;
  @Input() deviceSelectId: string;
  @Input() tooltipInfo: string;

  chartBarParts: ChartBarPartModel[];
  pointStart: number;

  initChartOptions: Highcharts.Options = {
    chart: {
      type: 'column',
    },
    scrollbar: {
      enabled: false,
    },
    credits: {
      enabled: false,
    },
    navigator: {
      enabled: false,
    },
    rangeSelector: {
      enabled: false,
    },
    legend: {
      enabled: false,
    },
    tooltip: {
      enabled: true,
      valueDecimals: 2,
      xDateFormat: '%A, %b %e, %Y',
    },
    plotOptions: {
      series: {
        visible: true,
        events: {
          legendItemClick: (e) => {
            e.preventDefault();
          },
        },
      },
      column: {
        pointIntervalUnit: 'day',
        grouping: false,
        dataLabels: {
          enabled: false,
        },
      },
    },
    title: {
      text: null,
    },
    series: [],
    xAxis: {
      labels: {
        enabled: true,
        align: 'center',
      },
      crosshair: false,
    },
    yAxis: {
      max: null,
      title: {
        text: undefined,
      },
    },
  };

  highCharts: typeof Highcharts = Highcharts;
  chart: Highcharts.Chart;
  chartOptions: Highcharts.Options;
  ngUnsubscribe$: Subject<void> = new Subject<void>();

  private isVolumeMetricUnit = true;

  constructor(
    @Inject(CONSTUMPTION_CHARTS_UNITS_ADAPTER_IT) protected unitsAdapter: UnitsAdapter,
    protected installationDetailsStore: InstallationDetailsStore,
    private translateService: TranslateService,
    private unitsStore: UnitsStore
  ) {}

  @HostListener('window:resize', ['$event']) onResize(): void {
    this.resizeChart();
  }

  @ViewChildren(MatTooltip)
  set matTooltip(queryList: QueryList<MatTooltip>) {
    matTooltipSetter(queryList);
  }

  ngOnInit(): void {
    this.unitsStore.isVolumeMetricUnit$.pipe(take(1)).subscribe((isMetric) => {
      this.isVolumeMetricUnit = isMetric;
    });

    Highcharts.setOptions({
      lang: {
        shortMonths: getEnumValues(MONTHS_SHORT).map((month_short) =>
          this.translateService.instant(month_short)
        ),
        shortWeekdays: getEnumValues(DAYS_SHORT).map((day_short) =>
          this.translateService.instant(day_short)
        ),
        weekdays: getEnumValues(WEEKDAYS).map((weekday) => this.translateService.instant(weekday)),
      },
    });
    this.chartBarParts = this.getTodayChartBarPart();

    this.initDataSeries();
    setTimeout(() => {
      this.resizeChart();
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.chartData.currentValue !== changes.chartData.previousValue) {
      this.updateSeries(changes.chartData.currentValue);
    }
  }

  setChartInstance(chart: Highcharts.Chart): void {
    this.chart = chart;
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe$.next();
    this.ngUnsubscribe$.complete();
  }

  private resizeChart(): void {
    this.chartOptions.chart.width = this.container.nativeElement.offsetWidth;
    this.chartOptions.chart.height = this.container.nativeElement.offsetHeight;

    try {
      this.chart.update(this.chartOptions, true);
    } catch (error) {
      // https://jira.viessmann-climatesolutions.com/browse/VIG-6174
      // sometimes we want to render the chart when it's hidden or not yet in the DOM
      // thus highcharts throws an exception, so we now swallow it.
      // it sometimes happens when we do a gateway reboot
    }
  }

  private initDataSeries(): void {
    const translate = this.translateService;
    this.initChartOptions.plotOptions.series.pointStart = this.pointStart;
    this.initChartOptions.chart.height = this.calculateChartHeight();
    this.initChartOptions.xAxis = {
      ...this.initChartOptions.xAxis,
      labels: {
        formatter(): string {
          return getFormatterForLocalizedDate(translate, this.value);
        },
      },
    };
    this.initChartOptions.navigator.xAxis = {
      ...this.initChartOptions.navigator.xAxis,
      labels: {
        formatter(): string {
          return getFormatterForLocalizedDate(translate, this.value);
        },
      },
    };

    const dayChartValues = this.chartData.periods.find(
      (entry) => entry.periodDesignationKeySingular === 'GENERAL.TIME_DATE.DAY'
    );
    this.setMaxChartValue(dayChartValues.maxValueOfThePeriod);

    this.chartOptions = {
      ...this.initChartOptions,
      chart: {
        ...this.initChartOptions.chart,
        height: this.chartBarParts.length > 2 ? 110 : 200,
      },
      series: this.getChartSeries(dayChartValues),
    };
  }

  private getChartSeries(chartData: ChartPeriod): Highcharts.SeriesOptionsType[] {
    let chartSeriesOptions: Highcharts.SeriesOptionsType[] = [];
    const flatStructure = this.getFlatDataStructure(chartData);
    const unitsAdapter = this.unitsAdapter;
    flatStructure.reverse().forEach((entry) => {
      const seriesData = entry.data.map((singlePoint) => {
        return this.unitsAdapter.getValue(singlePoint, entry.unit);
      });
      let seriesOptions: Highcharts.SeriesOptionsType = {
        id: entry.name,
        data: seriesData,
        color: viChartColors[entry.color] || viChartColors['vi-pending-00'],
        type: 'column',
        name: entry.name,
        maxPointWidth: 16,
        stacking: entry.part === 'TOTAL' ? undefined : 'normal',
        tooltip: {
          pointFormatter() {
            const unit = unitsAdapter.getSuffix(entry.unit);
            const value = unitsAdapter.getValue(this.y, unit);

            const displayValue = Math.round(value * 100) / 100;

            return `<span style='color: ${this.color}; margin-right: 5px'>\u25A0</span> ${this.series.name}: <strong>${displayValue} ${unit}</strong>`;
          },
        },
      };
      chartSeriesOptions.unshift(seriesOptions);
    });

    return chartSeriesOptions;
  }

  private getFlatDataStructure(
    chartData: ChartPeriod
  ): { name: string; part: string; data: number[]; unit: string; color: string }[] {
    let flatData: {
      name: string;
      part: string;
      data: number[];
      unit: string;
      color: string;
    }[] = [];
    chartData.chartBars.reverse().forEach((dayEntry) => {
      dayEntry.chartBarParts.forEach((entry, index) => {
        if (!flatData[index]) {
          flatData[index] = {
            name: this.translateService.instant(entry.designationKey),
            part: entry.designationKey.split('.').pop(),
            data: [],
            unit: entry.unit,
            color: entry.color,
          };
        }
        flatData[index].data = flatData[index].data.concat(entry.value);
      });
    });

    return flatData;
  }

  private getTodayChartBarPart(): ChartBarPartModel[] {
    const todayKey = 'DATETIME.TODAY';
    const daysPeriod = this.chartData.periods.find(
      (entry) => entry.periodDesignationKeySingular === 'GENERAL.TIME_DATE.DAY'
    );
    const periodStartDate = daysPeriod?.periodSummary[0]?.period?.start;

    const allChartBarParts: ChartBarPartModel[] = daysPeriod.chartBars.find(
      (entry) => entry.designationKey === todayKey
    ).chartBarParts;

    this.pointStart = periodStartDate
      ? moment.utc(new Date(periodStartDate)).valueOf()
      : moment.utc(new Date()).startOf('day').subtract(7, 'days').valueOf();

    const todayPeriod = allChartBarParts.slice(1, allChartBarParts.length);

    return todayPeriod;
  }

  private setMaxChartValue(value: number): void {
    if (this.overlayType === FeaturesDetailsOverlayTypeEnum.GasConsumption) {
      (this.initChartOptions.yAxis as any).max = this.isVolumeMetricUnit
        ? value
        : convertToCubicFeet(value);
    } else {
      (this.initChartOptions.yAxis as any).max = value;
    }
  }

  private calculateChartHeight(): number {
    const barRowsCount: number = Math.floor(this.chartBarParts?.length / 2);
    const maxBarRowsCount: number = Math.min(barRowsCount, 2);

    // 300px is maximal height for chart's space
    // As more legend bars (below the chart) are set up as the chart's height will be lower
    return 300 - 100 * maxBarRowsCount;
  }

  private updateSeries(seriesData): void {
    if (!this.chart) {
      return;
    }

    const dayChartValues = seriesData.periods.find(
      (entry) => entry.periodDesignationKeySingular === 'GENERAL.TIME_DATE.DAY'
    );
    this.setMaxChartValue(dayChartValues.maxValueOfThePeriod);

    const chartSeries = this.getChartSeries(dayChartValues);

    _.forEachRight(this.chart.series, (seriesItem) => {
      seriesItem.remove(false);
    });

    chartSeries.forEach((chartSeriesItem) => {
      this.chart.addSeries(chartSeriesItem, false);
    });

    this.resizeChart();
  }
}
